let mapsBootstrapPromise = null;

const REVIEWS_CACHE_PREFIX = 'sucursal_reviews_';
const REVIEWS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const REVIEWS_CACHE_CLEANUP_FLAG = 'sucursal_reviews_cache_cleanup_done';

const DEFAULT_STRINGS = {
  error:
    'No se pudieron cargar las reseñas. Revisa la API key y Places API (New) en Google Cloud.',
  empty: 'Aún no hay reseñas de Google para mostrar.',
  ratingLabel: 'Valoración',
  poweredBy: 'Reseñas de Google',
  previous: 'Reseña anterior',
  next: 'Siguiente reseña',
  counterOf: 'de',
  ratingSummary: '{{ rating }} de valoración de {{ count }} reseñas',
};

const GOOGLE_ICON_SVG = `<svg class="sucursal-reviews__google-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
</svg>`;

function resolveString(value, fallback) {
  if (!value || String(value).includes('Translation missing')) {
    return fallback;
  }
  return value;
}

function resolveStrings(strings) {
  const source = strings || {};
  return {
    error: resolveString(source.error, DEFAULT_STRINGS.error),
    empty: resolveString(source.empty, DEFAULT_STRINGS.empty),
    ratingLabel: resolveString(source.ratingLabel, DEFAULT_STRINGS.ratingLabel),
    poweredBy: resolveString(source.poweredBy, DEFAULT_STRINGS.poweredBy),
    previous: resolveString(source.previous, DEFAULT_STRINGS.previous),
    next: resolveString(source.next, DEFAULT_STRINGS.next),
    counterOf: resolveString(source.counterOf, DEFAULT_STRINGS.counterOf),
    ratingSummary: resolveString(source.ratingSummary, DEFAULT_STRINGS.ratingSummary),
  };
}

function getReviewsCacheKey(placeId) {
  return `${REVIEWS_CACHE_PREFIX}${placeId}`;
}

function canUseSessionStorage() {
  try {
    return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
  } catch {
    return false;
  }
}

function serializeReview(review) {
  if (!review) return null;

  const text = getReviewText(review);

  return {
    rating: review.rating,
    text,
    authorAttribution: {
      displayName: review.authorAttribution?.displayName || '',
      photoURI: review.authorAttribution?.photoURI || '',
    },
    relativePublishTimeDescription: review.relativePublishTimeDescription || '',
  };
}

function serializeReviews(reviews) {
  return (Array.isArray(reviews) ? reviews : [])
    .map((review) => serializeReview(review))
    .filter(Boolean);
}

function cleanupExpiredReviewsCache() {
  if (!canUseSessionStorage()) return;

  try {
    if (sessionStorage.getItem(REVIEWS_CACHE_CLEANUP_FLAG) === 'true') {
      return;
    }

    const now = Date.now();

    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (!key || !key.startsWith(REVIEWS_CACHE_PREFIX)) continue;

      try {
        const cached = JSON.parse(sessionStorage.getItem(key));
        if (!cached?.timestamp || now - cached.timestamp > REVIEWS_CACHE_TTL_MS) {
          sessionStorage.removeItem(key);
        }
      } catch {
        sessionStorage.removeItem(key);
      }
    }

    sessionStorage.setItem(REVIEWS_CACHE_CLEANUP_FLAG, 'true');
  } catch (error) {
    console.warn('[sucursal-google-reviews] Failed to cleanup reviews cache:', error);
  }
}

function getCachedReview(placeId) {
  if (!placeId || !canUseSessionStorage()) return null;

  cleanupExpiredReviewsCache();

  try {
    const cached = JSON.parse(sessionStorage.getItem(getReviewsCacheKey(placeId)));
    if (!cached?.timestamp) return null;

    if (Date.now() - cached.timestamp > REVIEWS_CACHE_TTL_MS) {
      sessionStorage.removeItem(getReviewsCacheKey(placeId));
      return null;
    }

    return cached;
  } catch {
    return null;
  }
}

function setCachedReview(placeId, data) {
  if (!placeId || !canUseSessionStorage() || !data) return;

  try {
    sessionStorage.setItem(
      getReviewsCacheKey(placeId),
      JSON.stringify({
        ...data,
        timestamp: Date.now(),
      })
    );
  } catch (error) {
    console.warn('[sucursal-google-reviews] Failed to store reviews cache:', error);
  }
}

function mapAggregateReviews(reviews, placeConfig, minRating) {
  return filterReviewsByMinRating(reviews, minRating).map((review) => ({
    rating: review.rating,
    text: review.text,
    authorAttribution: review.authorAttribution,
    relativePublishTimeDescription: review.relativePublishTimeDescription,
    storeName: placeConfig.storeName || '',
    storeHandle: placeConfig.storeHandle || '',
  }));
}

function hasEnoughAggregateReviews(reviewGroups, maxTotalReviews) {
  return mixReviewsRoundRobin(reviewGroups, maxTotalReviews).length >= maxTotalReviews;
}

async function fetchPlaceReviews(Place, placeConfig, minRating, fields) {
  const cached = getCachedReview(placeConfig.placeId);
  const requestedFields = Array.isArray(fields) ? fields : ['reviews'];
  const hasAllCachedFields =
    cached &&
    (!requestedFields.includes('reviews') || Array.isArray(cached.reviews)) &&
    (!requestedFields.includes('rating') || cached.rating != null) &&
    (!requestedFields.includes('userRatingCount') || cached.userRatingCount != null);

  if (hasAllCachedFields) {
    return {
      reviews: cached.reviews || [],
      rating: cached.rating,
      userRatingCount: cached.userRatingCount,
      fromCache: true,
    };
  }

  const place = new Place({ id: placeConfig.placeId });
  await place.fetchFields({ fields: requestedFields });

  const payload = {
    reviews: serializeReviews(place.reviews),
  };

  if (requestedFields.includes('rating')) {
    payload.rating = place.rating;
  }

  if (requestedFields.includes('userRatingCount')) {
    payload.userRatingCount = place.userRatingCount;
  }

  setCachedReview(placeConfig.placeId, {
    reviews: payload.reviews,
    rating: payload.rating ?? cached?.rating,
    userRatingCount: payload.userRatingCount ?? cached?.userRatingCount,
  });

  return {
    reviews: payload.reviews,
    rating: payload.rating,
    userRatingCount: payload.userRatingCount,
    fromCache: false,
  };
}

function installGoogleMapsBootstrap(apiKey) {
  if (typeof window.google?.maps?.importLibrary === 'function') {
    return Promise.resolve();
  }

  if (mapsBootstrapPromise) {
    return mapsBootstrapPromise;
  }

  mapsBootstrapPromise = new Promise((resolve, reject) => {
    try {
      ((options) => {
        var bootstrapPromise;
        var scriptEl;
        var optionKey;
        var apiName = 'The Google Maps JavaScript API';
        var googleNamespace = 'google';
        var importLibraryName = 'importLibrary';
        var callbackName = '__ib__';
        var doc = document;
        var win = window;

        win = win[googleNamespace] || (win[googleNamespace] = {});
        var mapsNamespace = win.maps || (win.maps = {});
        var librarySet = new Set();
        var searchParams = new URLSearchParams;

        var loadScript = () =>
          bootstrapPromise ||
          (bootstrapPromise = new Promise(async (onReady, onError) => {
            await (scriptEl = doc.createElement('script'));
            searchParams.set('libraries', [...librarySet] + '');
            for (optionKey in options) {
              searchParams.set(
                optionKey.replace(/[A-Z]/g, (char) => '_' + char[0].toLowerCase()),
                options[optionKey]
              );
            }
            searchParams.set('callback', googleNamespace + '.maps.' + callbackName);
            scriptEl.src = `https://maps.${googleNamespace}apis.com/maps/api/js?` + searchParams;
            mapsNamespace[callbackName] = onReady;
            scriptEl.onerror = () => {
              bootstrapPromise = onError(Error(apiName + ' could not load.'));
            };
            scriptEl.nonce = doc.querySelector('script[nonce]')?.nonce || '';
            doc.head.append(scriptEl);
          }));

        if (mapsNamespace[importLibraryName]) {
          console.warn(apiName + ' only loads once. Ignoring:', options);
        } else {
          mapsNamespace[importLibraryName] = (library, ...args) =>
            librarySet.add(library) && loadScript().then(() => mapsNamespace[importLibraryName](library, ...args));
        }
      })({
        key: apiKey,
        v: 'weekly',
        language: 'es',
        region: 'CO',
      });

      if (typeof window.google?.maps?.importLibrary === 'function') {
        resolve();
        return;
      }

      reject(new Error('importLibrary_not_installed'));
    } catch (error) {
      reject(error);
    }
  });

  return mapsBootstrapPromise;
}

function getReviewText(review) {
  if (!review?.text) return '';
  if (typeof review.text === 'string') return review.text;
  if (typeof review.text.text === 'string') return review.text.text;
  return '';
}

function filterReviewsByMinRating(reviews, minRating) {
  return (Array.isArray(reviews) ? reviews : []).filter(
    (review) => Number(review.rating) >= minRating
  );
}

function renderStars(rating) {
  const value = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
  let html = '';

  for (let i = 1; i <= 5; i += 1) {
    html += `<span class="sucursal-reviews__star${i <= value ? ' is-filled' : ''}" aria-hidden="true">★</span>`;
  }

  return html;
}

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatRatingSummary(template, rating, count) {
  return template
    .replace(/\{\{\s*rating\s*\}\}/g, escapeHtml(rating))
    .replace(/\{\{\s*count\s*\}\}/g, escapeHtml(count));
}

function updateHeaderSummary(root, rating, count, strings) {
  const summaryEl = root?.closest('.sucursal-reviews')?.querySelector('[data-reviews-summary]');
  if (!summaryEl || !rating) return;

  const reviewCount = count || 0;
  summaryEl.innerHTML = `
    <div class="sucursal-reviews__summary-rating" aria-label="${escapeHtml(strings.ratingLabel)}">
      <span class="sucursal-reviews__stars">${renderStars(rating)}</span>
      <span class="sucursal-reviews__summary-text">${formatRatingSummary(strings.ratingSummary, rating.toFixed(1), reviewCount)}</span>
    </div>
  `;
  summaryEl.hidden = false;
}

function mixReviewsRoundRobin(reviewGroups, maxTotal) {
  const queues = reviewGroups
    .filter((group) => Array.isArray(group) && group.length)
    .map((group) => group.slice());
  const mixed = [];
  let added = true;

  while (added && mixed.length < maxTotal) {
    added = false;

    for (const queue of queues) {
      if (mixed.length >= maxTotal) break;
      if (queue.length) {
        mixed.push(queue.shift());
        added = true;
      }
    }
  }

  return mixed;
}

function renderReviewCard(review, strings) {
  const author = review.authorAttribution?.displayName || '';
  const time = review.relativePublishTimeDescription || '';
  const storeName = review.storeName || '';
  const text = getReviewText(review);
  const rating = review.rating || 0;
  const photoUri = review.authorAttribution?.photoURI || '';
  const avatarHtml = photoUri
    ? `<img class="sucursal-reviews__avatar" src="${escapeHtml(photoUri)}" alt="" width="40" height="40" loading="lazy">`
    : `<span class="sucursal-reviews__avatar sucursal-reviews__avatar--placeholder" aria-hidden="true">${escapeHtml(author.charAt(0) || '?')}</span>`;

  return `
    <article class="sucursal-reviews__card">
      <div class="sucursal-reviews__card-top">
        <div class="sucursal-reviews__stars" aria-label="${escapeHtml(strings.ratingLabel)}">
          ${renderStars(rating)}
        </div>
        <span class="sucursal-reviews__google-badge">${GOOGLE_ICON_SVG}</span>
      </div>
      ${text ? `<blockquote class="sucursal-reviews__text"><p>${escapeHtml(text)}</p></blockquote>` : ''}
      <footer class="sucursal-reviews__card-footer">
        ${avatarHtml}
        <div class="sucursal-reviews__card-meta">
          ${author ? `<strong class="sucursal-reviews__author">${escapeHtml(author)}</strong>` : ''}
          ${storeName ? `<span class="sucursal-reviews__store">${escapeHtml(storeName)}</span>` : ''}
          ${time ? `<span class="sucursal-reviews__time">${escapeHtml(time)}</span>` : ''}
        </div>
      </footer>
    </article>
  `;
}

function renderCarousel(reviews, strings, placeId) {
  const cardsHtml = reviews.map((review) => renderReviewCard(review, strings)).join('');
  const mapsUrl = placeId
    ? `https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${encodeURIComponent(placeId)}`
    : 'https://www.google.com/maps';

  return `
    <sucursal-reviews-carousel class="sucursal-reviews__carousel">
      <div class="sucursal-reviews__carousel-shell">
        <button
          type="button"
          class="sucursal-reviews__nav sucursal-reviews__nav--prev"
          data-action="prev"
          aria-label="${escapeHtml(strings.previous)}"
        >
          <span class="svg-wrapper" aria-hidden="true">
            <svg viewBox="0 0 10 6" width="10" height="6" focusable="false">
              <path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" transform="rotate(90 5 3)"/>
            </svg>
          </span>
        </button>
        <div class="sucursal-reviews__track">
          <div
            class="sucursal-reviews__slides"
            role="region"
            aria-label="${escapeHtml(strings.poweredBy)}"
            tabindex="0"
          >
            ${cardsHtml}
          </div>
        </div>
        <button
          type="button"
          class="sucursal-reviews__nav sucursal-reviews__nav--next"
          data-action="next"
          aria-label="${escapeHtml(strings.next)}"
        >
          <span class="svg-wrapper" aria-hidden="true">
            <svg viewBox="0 0 10 6" width="10" height="6" focusable="false">
              <path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" transform="rotate(-90 5 3)"/>
            </svg>
          </span>
        </button>
      </div>
      <p class="sucursal-reviews__counter caption" aria-live="polite">
        <span class="visually-hidden">${escapeHtml(strings.ratingLabel)}</span>
        <span data-carousel-current>1</span>
        <span aria-hidden="true"> ${escapeHtml(strings.counterOf)} </span>
        <span data-carousel-total>1</span>
      </p>
      <p class="sucursal-reviews__attribution">
        <a href="${mapsUrl}" target="_blank" rel="noopener noreferrer">${escapeHtml(strings.poweredBy)}</a>
      </p>
    </sucursal-reviews-carousel>
  `;
}

if (!customElements.get('sucursal-google-reviews')) {
  customElements.define(
    'sucursal-google-reviews',
    class SucursalGoogleReviews extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';
        this.loadReviews();
      }

      getConfig() {
        const configId = this.dataset.configId;
        const configEl = configId ? document.getElementById(configId) : null;

        if (!configEl) return null;

        try {
          return JSON.parse(configEl.textContent);
        } catch {
          return null;
        }
      }

      showMessage(className, message) {
        this.innerHTML = `<p class="${className}">${escapeHtml(message)}</p>`;
      }

      getPlaces(config) {
        if (!Array.isArray(config?.places)) return [];
        return config.places.filter((place) => place?.placeId);
      }

      async loadReviews() {
        const config = this.getConfig();
        const strings = resolveStrings(config?.strings);
        const places = this.getPlaces(config);
        const isAggregate = places.length > 0;

        if (!config?.apiKey || (!isAggregate && !config?.placeId)) {
          this.showMessage('sucursal-reviews__error', strings.error);
          return;
        }

        if (isAggregate) {
          await this.loadAggregateReviews(config, strings, places);
          return;
        }

        await this.loadSingleBranchReviews(config, strings);
      }

      async loadSingleBranchReviews(config, strings) {
        const minRating = Number(config.minRating) || 4;
        const maxReviews = Math.max(1, Math.min(5, Number(config.maxReviews) || 5));
        const showAggregate = Boolean(config.showAggregateRating);
        const placeConfig = { placeId: config.placeId };
        const fields = showAggregate
          ? ['rating', 'userRatingCount', 'reviews']
          : ['reviews'];

        try {
          const cached = getCachedReview(config.placeId);
          const hasCachedData =
            cached &&
            (!fields.includes('reviews') || Array.isArray(cached.reviews)) &&
            (!fields.includes('rating') || cached.rating != null) &&
            (!fields.includes('userRatingCount') || cached.userRatingCount != null);

          let placeData;

          if (hasCachedData) {
            placeData = {
              reviews: cached.reviews || [],
              rating: cached.rating,
              userRatingCount: cached.userRatingCount,
            };
          } else {
            await installGoogleMapsBootstrap(config.apiKey);
            const { Place } = await google.maps.importLibrary('places');
            placeData = await fetchPlaceReviews(Place, placeConfig, minRating, fields);
          }

          const reviews = filterReviewsByMinRating(placeData.reviews, minRating).slice(0, maxReviews);

          if (!reviews.length && !showAggregate) {
            this.showMessage('sucursal-reviews__empty', strings.empty);
            return;
          }

          if (showAggregate && placeData.rating) {
            updateHeaderSummary(this, placeData.rating, placeData.userRatingCount, strings);
          }

          if (reviews.length) {
            this.innerHTML = renderCarousel(reviews, strings, config.placeId);
            return;
          }

          this.showMessage('sucursal-reviews__empty', strings.empty);
        } catch (error) {
          console.error('[sucursal-google-reviews] Failed to load Google reviews:', error);
          this.showMessage('sucursal-reviews__error', strings.error);
        }
      }

      async loadAggregateReviews(config, strings, places) {
        const minRating = Number(config.minRating) || 4;
        const maxTotalReviews = Math.max(1, Math.min(15, Number(config.maxTotalReviews) || 15));
        const abortController = new AbortController();
        const reviewGroups = [];
        const uncachedPlaces = [];

        try {
          for (const placeConfig of places) {
            if (abortController.signal.aborted) break;

            const cached = getCachedReview(placeConfig.placeId);
            if (cached?.reviews) {
              reviewGroups.push(mapAggregateReviews(cached.reviews, placeConfig, minRating));

              if (hasEnoughAggregateReviews(reviewGroups, maxTotalReviews)) {
                abortController.abort();
                break;
              }
              continue;
            }

            uncachedPlaces.push(placeConfig);
          }

          if (!abortController.signal.aborted && uncachedPlaces.length) {
            await installGoogleMapsBootstrap(config.apiKey);
            const { Place } = await google.maps.importLibrary('places');

            for (const placeConfig of uncachedPlaces) {
              if (abortController.signal.aborted) break;

              try {
                const placeData = await fetchPlaceReviews(Place, placeConfig, minRating, ['reviews']);
                reviewGroups.push(mapAggregateReviews(placeData.reviews, placeConfig, minRating));

                if (hasEnoughAggregateReviews(reviewGroups, maxTotalReviews)) {
                  abortController.abort();
                  break;
                }
              } catch (error) {
                console.error(
                  '[sucursal-google-reviews] Failed to load reviews for',
                  placeConfig.placeId,
                  error
                );
              }
            }
          }

          const reviews = mixReviewsRoundRobin(reviewGroups, maxTotalReviews);

          if (!reviews.length) {
            this.showMessage('sucursal-reviews__empty', strings.empty);
            return;
          }

          this.innerHTML = renderCarousel(reviews, strings, '');
        } catch (error) {
          console.error('[sucursal-google-reviews] Failed to load Google reviews:', error);
          this.showMessage('sucursal-reviews__error', strings.error);
        }
      }
    }
  );
}
